import { patchOuter } from 'incremental-dom';
import {
  INITIAL_MAP_ONLINE_SERVICE,
  type State,
} from '../../state/createInitialState';
import type { SimpleStore } from '../../state/SimpleStore';
import { MAP_ELEMENT_ID, MAP_OUTER_ELEMENT_ID } from '../mapConstants';
import { getNextInstanceNumber } from './mapRegistry';
import {
  DATA_MAP_ID,
  DATA_MAP_INSTANCE_ID,
  patchMapFrameAndOuterElements,
} from './patchMapFrameAndOuterElements';
import {
  cleanupLeafletMapInstances,
  insertLeafletMap,
  setLastRenderedStore,
  updateLeafletMap,
} from './renderLeafletMap';
import { calcMapInstanceId } from './util/calcMapInstanceId';

/**
 * In jedes der outerElements eine Karte rendern oder diese aktualisieren.
 *
 * Hinweis: Es müssen immer alle Elemente angegeben werden, die eine Karte beinhalten. Hintergrund: für alle Karten
 * werden intern Zustände gespeichert. Diese Funktion entfernt alle internen Zustände, die sich auf keine der hier
 * angegebenen Karten beziehen.
 *
 * @param outerElements Elemente, in die eine Karte eingefügt oder eine bestehende Karte aktualisiert werden soll
 * @param simpleStore Store mit Zuständen
 */
export function renderOnlinediensteMapSectionInternal(
  outerElements: Element[],
  simpleStore: SimpleStore,
) {
  setLastRenderedStore(simpleStore);

  const state = simpleStore.getState();

  // wird im Verlauf der Funktion mit den IDs der Karten in outerElements gefüllt
  const existingInstances: string[] = [];

  outerElements.forEach((outerElement) => {
    const instanceId = renderMapIntoDomElement(
      state,
      simpleStore,
      outerElement,
    );
    if (instanceId !== undefined) {
      // Merke die Instanz-ID der Karte, um am Ende der Methode alle Zustände von nicht mehr im DOM existierenden Karten aufräumen zu können
      existingInstances.push(instanceId);
    }
  });

  // Interne Zustände für nicht mehr existierende Karten löschen
  cleanupLeafletMapInstances(existingInstances);
}

function renderMapIntoDomElement(
  state: State,
  simpleStoreForHandlers: SimpleStore,
  outerElement: Element,
): string | undefined {
  // Daten und Zustand aller Karten
  const stateMap = state.mapOnlineServices;
  // DOM-ID der Karte aus DOM-Attribut auslesen (um die zugehörigen DOM-Elemente zu finden; nicht zu verwechseln mit der Instanz-ID von Karten)
  const mapDomId = outerElement.getAttribute(DATA_MAP_ID) ?? undefined;
  if (!(outerElement instanceof HTMLElement)) {
    console.error('outerElement not instanceof HTMLElement', outerElement);
    return undefined;
  }
  if (!mapDomId) {
    console.error('mapDomId is missing on outerElement', outerElement);
    return undefined;
  }

  // Zustand der Karte holen
  const mapState = stateMap.mapStates[mapDomId] ?? INITIAL_MAP_ONLINE_SERVICE;

  // werden aktuell Daten nachgeladen?
  const loading =
    Boolean(state.abortRequest.mapOnlineServicesCounts) ||
    Boolean(state.abortRequest.mapOnlineServicesData);

  // Äußeren Rahmen der Karte und rechte Spalte neben der Karte rendern/aktualisieren
  patchOuter(
    outerElement,
    patchMapFrameAndOuterElements(
      simpleStoreForHandlers,
      state.flags,
      stateMap.mapData,
      stateMap.onlineServicesByRegschl,
      mapState,
      state.citySearch.selectedOrt.id,
      mapDomId,
      loading,
    ),
  );

  // Mache nur weiter, falls übergreifende Basisdaten bereits nachgeladen wurden
  if (!stateMap.mapData) {
    return undefined;
  }

  // das DOM-Element der Karte holen. Existiert nur, falls die Karte bereits gerendert wurde
  const existingMap =
    document.getElementById(MAP_ELEMENT_ID(mapDomId)) ?? undefined;
  if (!existingMap) {
    // Karte wurde noch nicht gerendert
    // Hole das Container-DOM-Element, in welches die Karte gerendert werden soll
    const mapContainerElement =
      document.getElementById(MAP_OUTER_ELEMENT_ID(mapDomId)) ?? undefined;
    if (!mapContainerElement) {
      console.error(`${MAP_OUTER_ELEMENT_ID(mapDomId)} is missing`);
      return undefined;
    }

    // Erzeuge eine neue Instanz-ID der Karte (zusammengesetzt aus der DOM-ID der Karte und einer global fortlaufenden Nummer)
    const instanceNumber = getNextInstanceNumber();
    const mapInstanceId = calcMapInstanceId(mapDomId, instanceNumber);

    // erzeuge das äußere Element der Karte
    const mapElement = document.createElement('div');
    mapElement.id = MAP_ELEMENT_ID(mapDomId);
    // Merke die DOM-ID und die Instanz-ID der Karte in Data-Attributen am DOM-Element
    mapElement.setAttribute(DATA_MAP_ID, mapDomId);
    mapElement.setAttribute(DATA_MAP_INSTANCE_ID, mapInstanceId);
    // füge das DOM-Element in den Container ein
    mapContainerElement.append(mapElement);

    // erzeuge die Leaflet-Karte
    insertLeafletMap(
      mapElement,
      stateMap.mapData.kommunen,
      stateMap.mapData.landkreise,
    );
  }
  return updateLeafletMap(mapDomId, mapState);
}
